/**
 * toolLabels.ts — Conversational first-person labels for AI tool calls.
 *
 * Ported 1:1 from secretary-pwa/src/utils/toolLabels.js so the Laabha
 * tool-call stream uses identical phrasing to dev-yes.axonbos.com.
 *
 * Each tool has two states:
 *   active — shown with spinner while running ("Let me…", "I'm…")
 *   done   — shown with checkmark after completion ("I found…", "I've sent…")
 */

type State = 'active' | 'done';

interface ToolEntry {
  active: string;
  done: string;
  detailFn?: (params: Record<string, unknown>) => string | null;
}

// ── Core Secretary / Brain Tools ────────────────────────────────────────────
const TOOL_LABELS: Record<string, ToolEntry> = {
  create_task: {
    active: 'Let me create that task…',
    done: "I've created the task",
    detailFn: (p) => (p.title as string) || null,
  },
  list_tasks: {
    active: 'Let me check your tasks…',
    done: "I've looked at your tasks",
  },
  create_followup: {
    active: "I'm scheduling a follow-up…",
    done: "I've scheduled the follow-up",
    detailFn: (p) => (p.contact_name as string) || (p.note as string) || null,
  },
  search_contacts: {
    active: 'Let me search your contacts…',
    done: 'I found your contacts',
    detailFn: (p) => (p.query as string) || null,
  },
  search_memory: {
    active: 'Let me think back…',
    done: 'I recalled something relevant',
    detailFn: (p) => (p.query as string) || null,
  },
  store_memory: {
    active: "I'm saving this to memory…",
    done: "I've saved that",
  },
  get_my_day: {
    active: 'Let me check your day…',
    done: "I've looked at your day",
  },
  create_topic: {
    active: "I'm creating a topic…",
    done: "I've created the topic",
    detailFn: (p) => (p.name as string) || null,
  },
  list_topics: {
    active: 'Let me check your topics…',
    done: "I've looked at your topics",
  },
  create_calendar_event: {
    active: "I'm adding this to your calendar…",
    done: "I've added it to your calendar",
    detailFn: (p) => (p.title as string) || (p.summary as string) || null,
  },
  create_contact: {
    active: "I'm saving this contact…",
    done: "I've saved the contact",
    detailFn: (p) => (p.name as string) || null,
  },
  create_transaction: {
    active: "I'm recording the transaction…",
    done: "I've recorded the transaction",
  },
  generate_auth_link: {
    active: 'Let me connect that app…',
    done: "I've generated the connection link",
    detailFn: (p) => (p.app as string) || null,
  },
  list_connections: {
    active: 'Let me check your connected apps…',
    done: "I've checked your connections",
  },
  search_vault: {
    active: 'Let me search the vault…',
    done: 'I found something in the vault',
    detailFn: (p) => (p.query as string) || null,
  },
  platform_list_tasks: {
    active: 'Let me check the tasks…',
    done: "I've checked the tasks",
  },
  platform_create_task: {
    active: "I'm creating a task…",
    done: "I've created the task",
    detailFn: (p) => (p.title as string) || null,
  },
  platform_send_notification: {
    active: "I'm sending a notification…",
    done: "I've sent the notification",
  },
  // ── AIAccountant / TALLY-DE ─────────────────────────────────────────────
  ingest_bank_statement: {
    active: "I'm parsing your bank statement…",
    done: 'Statement parsed',
  },
  confirm_import: {
    active: "I'm pushing vouchers to Tally…",
    done: 'Pushed to Tally',
  },
  claude_parse_unknown: {
    active: "Reading the file with Claude vision…",
    done: 'Read the file',
  },
};

// ── Composio Action Labels — key format "appslug.ACTION_SLUG" ───────────────
const COMPOSIO_ACTION_LABELS: Record<string, { active: string; done: string }> = {
  // Gmail
  'gmail.GMAIL_SEND_EMAIL':                  { active: "I'm sending the email…",            done: "I've sent the email" },
  'gmail.GMAIL_FETCH_EMAILS':                { active: 'Let me check your Gmail…',           done: "I've checked your inbox" },
  'gmail.GMAIL_REPLY_TO_THREAD':             { active: "I'm replying to that thread…",       done: "I've sent the reply" },
  'gmail.GMAIL_FETCH_MESSAGE_BY_MESSAGE_ID': { active: 'Let me read that email…',            done: "I've read the email" },
  'gmail.GMAIL_LIST_THREADS':                { active: 'Let me look through your threads…',  done: "I've scanned your threads" },
  'gmail.GMAIL_LIST_LABELS':                 { active: 'Let me check your Gmail labels…',    done: "I've checked your labels" },
  // Google Calendar
  'googlecalendar.GOOGLECALENDAR_CREATE_EVENT':    { active: "I'm adding this to your calendar…", done: "I've added it to your calendar" },
  'googlecalendar.GOOGLECALENDAR_EVENTS_LIST':     { active: 'Let me check your calendar…',        done: "I've checked your schedule" },
  'googlecalendar.GOOGLECALENDAR_FIND_FREE_SLOTS': { active: 'Let me find a free slot…',            done: 'I found some open times' },
  'googlecalendar.GOOGLECALENDAR_DELETE_EVENT':    { active: "I'm removing that event…",            done: "I've removed the event" },
  'googlecalendar.GOOGLECALENDAR_FIND_EVENT':      { active: 'Let me find that event…',             done: 'I found the event' },
  // Google Drive
  'googledrive.GOOGLEDRIVE_FIND_FILE':              { active: 'Let me check Google Drive…',        done: 'I found the file' },
  'googledrive.GOOGLEDRIVE_LIST_FILES':             { active: 'Let me look through Drive…',        done: "I've checked Drive" },
  'googledrive.GOOGLEDRIVE_CREATE_FILE_FROM_TEXT':  { active: "I'm creating the file in Drive…",   done: "I've created the file" },
  'googledrive.GOOGLEDRIVE_FIND_FOLDER':            { active: 'Let me find that folder…',          done: 'I found the folder' },
  // Google Sheets
  'googlesheets.GOOGLESHEETS_BATCH_UPDATE':              { active: "I'm updating the sheet…",   done: "I've updated the sheet" },
  'googlesheets.GOOGLESHEETS_BATCH_GET':                 { active: 'Let me read the sheet…',    done: "I've read the sheet" },
  'googlesheets.GOOGLESHEETS_CREATE_SPREADSHEET_ROW':    { active: "I'm adding a row…",         done: "I've added the row" },
  'googlesheets.GOOGLESHEETS_CREATE_SPREADSHEET_COLUMN': { active: "I'm adding a column…",      done: "I've added the column" },
  // Slack
  'slack.SLACK_SEND_MESSAGE':               { active: "I'm sending the Slack message…",     done: "I've sent the Slack message" },
  'slack.SLACK_FETCH_CONVERSATION_HISTORY': { active: 'Let me read the Slack thread…',      done: "I've read the conversation" },
  'slack.SLACK_FIND_CHANNELS':              { active: 'Let me find that channel…',          done: 'I found the channel' },
  'slack.SLACK_LIST_ALL_CHANNELS':          { active: 'Let me check your Slack channels…',  done: "I've listed the channels" },
  // GitHub
  'github.GITHUB_CREATE_AN_ISSUE':              { active: "I'm creating the GitHub issue…", done: "I've created the issue" },
  'github.GITHUB_CREATE_AN_ISSUE_COMMENT':      { active: "I'm posting a comment…",         done: "I've posted the comment" },
  'github.GITHUB_LIST_ISSUES_FOR_A_REPOSITORY': { active: 'Let me check the issues…',       done: "I've pulled the issues" },
};

/**
 * Returns a conversational label + optional detail for a tool call.
 */
export function toolLabel(
  name: string,
  params: Record<string, unknown> = {},
  state: State = 'done',
): { label: string; detail: string | null } {
  // 1. Direct match in core tool map
  const entry = TOOL_LABELS[name];
  if (entry) {
    return {
      label: entry[state] || entry.done,
      detail: entry.detailFn ? entry.detailFn(params) : null,
    };
  }

  // 2. Composio pattern
  if (name.startsWith('composio_')) {
    const app = name.replace('composio_', '');
    const actions = (params.actions as Array<{ action?: string }> | undefined) ?? [];
    const action =
      (params.action as string | undefined) || actions[0]?.action || '';
    const composioEntry = COMPOSIO_ACTION_LABELS[`${app}.${action}`];
    if (composioEntry) {
      return { label: composioEntry[state] || composioEntry.done, detail: null };
    }
    return {
      label: state === 'active' ? `Let me use ${app}…` : `Used ${app}`,
      detail: null,
    };
  }

  // 3. Fallback — never expose raw tool name
  return {
    label: state === 'active' ? 'Let me work on that…' : 'Done',
    detail: null,
  };
}

/** Compact summary of paramsSummary for display under a label. */
export function formatParams(
  summary: Record<string, unknown> | null | undefined,
): string | null {
  if (!summary || typeof summary !== 'object') return null;
  const keys = Object.keys(summary);
  if (keys.length === 0) return null;
  return keys
    .slice(0, 3)
    .map((k) => `${k}: ${String(summary[k])}`)
    .join(' · ');
}
